import {
  column,
  Css,
  fieldColumn,
  getTableStyles,
  GridColumn,
  GridTable,
  Icon,
  IconKey,
  Palette,
  RowStyles,
  simpleHeader,
  SimpleHeaderAndData,
} from "@homebound/beam";
import { FieldState, ObjectState } from "@homebound/form-state";
import { camelCase } from "change-case";
import { ReactNode, useMemo } from "react";
import { BoundAiNumberField } from "src/components/formFields/ai";
import { AiFieldProposalFormValue } from "src/components/formFields/ai/utils/aiFieldProposalForm";
import { AiFieldProposalStatus, Maybe } from "src/generated/graphqlTypes";

export type ProgramDataFeature = {
  label: string;
  base: FieldState<Maybe<number>>;
  min: Maybe<number>;
  max: Maybe<number>;
  aiProposal?: ObjectState<AiFieldProposalFormValue>;
};

type ProgramDataCardProps = {
  title: string;
  icon: IconKey;
  /** Enums have no min/max, so they render above the table rather than as a feature row. */
  enumField?: ReactNode;
  features?: ProgramDataFeature[];
  disabled: ReactNode | undefined;
};

export function ProgramDataCard(props: ProgramDataCardProps) {
  const { title, icon, enumField, features, disabled } = props;
  const columns = useMemo(() => createColumns(disabled), [disabled]);
  const rows = useMemo(
    () => [simpleHeader, ...(features ?? []).map((f) => ({ id: f.label, kind: "data" as const, data: f }))],
    [features],
  );

  return (
    <div css={Css.df.fdc.gap1.$}>
      <div css={Css.df.aic.gap1.$}>
        <Icon icon={icon} />
        <h3 css={Css.mdSb.$}>{title}</h3>
      </div>
      {enumField && <div css={Css.py1.$}>{enumField}</div>}
      {features && (
        <GridTable
          id={`${camelCase(title)}Table`}
          columns={columns}
          rows={rows}
          style={tableStyle}
          rowStyles={rowStyles}
        />
      )}
    </div>
  );
}

type Row = SimpleHeaderAndData<ProgramDataFeature>;

const tableStyle = {
  ...getTableStyles({ bordered: true, allWhite: true, inlineEditing: true, vAlign: "top" }),
  rootCss: Css.mw0.$,
};

// GridTable rows are observers, so reading proposal state here re-renders only this row.
// Hover uses `--beam-row-hover-bg` (Beam paints `.beam-row-hover:hover > *` from that var).
const proposedHighlightCss = Css.bgPurple50.style({ "--beam-row-hover-bg": Palette.Purple100 }).$;
const rowStyles: RowStyles<Row> = {
  data: { cellCss: ({ data }) => (hasProposedHighlight(data) ? proposedHighlightCss : {}) },
};

function hasProposedHighlight(feature: ProgramDataFeature): boolean {
  return feature.aiProposal?.status.value === AiFieldProposalStatus.Pending;
}

function createColumns(disabled: ReactNode | undefined): GridColumn<Row>[] {
  return [
    column<Row>({ header: "Feature", data: ({ label }) => label }),
    fieldColumn<Row>({
      header: "Base",
      data: ({ label, base, aiProposal }) => (
        <BoundAiNumberField
          label={label}
          labelStyle="hidden"
          field={base}
          aiProposal={aiProposal}
          disabled={disabled}
          xss={Css.tal.jcfs.$}
        />
      ),
      w: "100px",
    }),
    column<Row>({ header: "Min", data: ({ min }) => min ?? "—", w: "80px" }),
    column<Row>({ header: "Max", data: ({ max }) => max ?? "—", w: "80px" }),
  ];
}
